import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  companyId: string;
}

const CSVDestinationExport = ({ companyId }: Props) => {
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      // Fetch all company countries with country details
      const { data: companyCountries, error } = await supabase
        .from('company_countries')
        .select(`
          is_open,
          notes,
          countries (
            name
          )
        `)
        .eq('company_id', companyId);

      if (error) throw error;

      if (!companyCountries || companyCountries.length === 0) {
        toast({
          title: "Aucune destination",
          description: "Aucune destination à exporter"
        });
        return;
      }

      // Build CSV content
      const header = 'Country,City,Status,Notes\n';
      const rows = companyCountries.map((cc: any) => {
        const countryName = cc.countries?.name || '';
        const status = cc.is_open ? 'Open' : 'Closed';
        const notes = cc.notes ? `"${cc.notes.replace(/"/g, '""')}"` : '';
        return `${countryName},,${status},${notes}`;
      }).join('\n');

      const csvContent = header + rows;

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `destinations_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      // Save export history
      await supabase
        .from('destination_import_export_history')
        .insert({
          company_id: companyId,
          admin_user_id: user.id,
          operation_type: 'export',
          file_name: link.download,
          total_processed: companyCountries.length,
          countries_opened: companyCountries.filter((cc: any) => cc.is_open).length,
          countries_closed: companyCountries.filter((cc: any) => !cc.is_open).length,
          errors_count: 0
        });

      toast({
        title: "Export réussi",
        description: `${companyCountries.length} destinations exportées`
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'export",
        description: error.message
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button onClick={handleExport} variant="outline" disabled={exporting}>
      <Download className="mr-2 h-4 w-4" />
      {exporting ? 'Export en cours...' : 'Exporter destinations en CSV'}
    </Button>
  );
};

export default CSVDestinationExport;
